import { lazy, Suspense } from 'react';
import { motion } from 'framer-motion';
import PageNavbar from '../../components/feature/PageNavbar';
import Footer from '../home/components/Footer';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';
import { useSEO, generateWebPageSchema, generatePersonSchema } from '../../utils/seo';

// Alle Sektionen unterhalb des Hero lazy laden
const AboutMission = lazy(() => Promise.resolve({ default: AboutMissionSection }));
const AboutValues = lazy(() => Promise.resolve({ default: AboutValuesSection }));
const AboutExpertise = lazy(() => Promise.resolve({ default: AboutExpertiseSection }));
const AboutCTA = lazy(() => Promise.resolve({ default: AboutCTASection }));

function AboutMissionSection() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-5xl font-caprasimo mb-6 text-brand-black">Meine Mission</h2>
            <p className="text-lg text-brand-grey mb-6 leading-relaxed">
              Ich glaube daran, dass jedes Unternehmen eine Webseite verdient, die nicht nur gut aussieht, sondern
              auch echte Ergebnisse liefert. Deshalb kombiniere ich modernes Webdesign mit intelligenter AI-Technologie.
            </p>
            <p className="text-lg text-brand-grey mb-6 leading-relaxed">
              Als Gründer von <strong>BR Webdesign & AI Solutions</strong> helfe ich Unternehmen dabei, ihre
              Online-Präsenz in eine leistungsstarke Verkaufsmaschine zu verwandeln – mit maßgeschneiderten Webseiten,
              AI-Chatbots und datengetriebener SEO-Optimierung.
            </p>
            <div className="flex flex-col gap-3 mt-8">
              <a href="tel:01623651571" className="flex items-center gap-3 text-brand-green font-semibold hover:gap-4 transition-all cursor-pointer">
                <div className="w-10 h-10 flex items-center justify-center bg-brand-green/10 rounded-lg">
                  <i className="ri-phone-line text-brand-green"></i>
                </div>
                0162 365 1571
              </a>
              <a href="mailto:info-aiagents@web.de" className="flex items-center gap-3 text-brand-green font-semibold hover:gap-4 transition-all cursor-pointer">
                <div className="w-10 h-10 flex items-center justify-center bg-brand-green/10 rounded-lg">
                  <i className="ri-mail-line text-brand-green"></i>
                </div>
                info-aiagents@web.de
              </a>
            </div>
          </div>
          <div className="relative">
            <img
              src="https://public.readdy.ai/ai/img_res/f0b6539067bb233f5cbc5ea5eaf97a79.jpg"
              alt="Benjamin Reick – BR Webdesign & AI Solutions"
              className="w-full h-[500px] object-cover object-top rounded-2xl border border-brand-beige shadow-xl"
              loading="lazy"
              decoding="async"
              width={600}
              height={500}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function AboutValuesSection() {
  const values = [
    { icon: 'ri-heart-line', title: 'Persönlich & Direkt', description: 'Kein Callcenter, kein Ticket-System. Sie sprechen immer direkt mit mir – schnell, unkompliziert und auf Augenhöhe.' },
    { icon: 'ri-lightbulb-line', title: 'Innovation & AI', description: 'Ich setze auf modernste Technologien und AI-Lösungen, um Ihrem Unternehmen einen echten Wettbewerbsvorteil zu verschaffen.' },
    { icon: 'ri-line-chart-line', title: 'Ergebnisorientiert', description: 'Schönes Design allein reicht nicht. Jede Webseite, die ich erstelle, ist darauf ausgelegt, messbare Ergebnisse zu liefern.' },
    { icon: 'ri-shield-check-line', title: 'Qualität & Sicherheit', description: 'Sauberer Code, DSGVO-Konformität und höchste Sicherheitsstandards sind bei mir selbstverständlich.' },
    { icon: 'ri-speed-line', title: 'Performance First', description: 'Schnelle Ladezeiten und optimale Core Web Vitals sind die Grundlage für gute Rankings und zufriedene Besucher.' },
    { icon: 'ri-hand-heart-line', title: 'Langfristige Partnerschaft', description: 'Ich bin nicht nur für den Launch da – ich begleite Sie langfristig mit Wartung, Optimierung und strategischer Beratung.' },
  ];
  return (
    <section className="py-20 bg-brand-beige-light">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-caprasimo mb-6 text-brand-black">Meine Werte</h2>
          <p className="text-lg text-brand-grey max-w-3xl mx-auto">Diese Prinzipien leiten meine Arbeit bei jedem Projekt</p>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {values.map((value, index) => (
            <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }} viewport={{ once: true }} className="bg-white rounded-xl p-8 border border-brand-beige hover:border-brand-green hover:shadow-xl transition-all group">
              <div className="w-12 h-12 flex items-center justify-center bg-brand-beige-light rounded-lg mb-6 group-hover:bg-brand-green group-hover:scale-110 transition-all">
                <i className={`${value.icon} text-2xl text-brand-green group-hover:text-white transition-colors`}></i>
              </div>
              <h3 className="text-xl font-caprasimo text-brand-black mb-4 group-hover:text-brand-green transition-colors">{value.title}</h3>
              <p className="text-brand-grey leading-relaxed text-sm">{value.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AboutExpertiseSection() {
  const skills = [
    { icon: 'ri-layout-masonry-line', label: 'Neue Webseiten' },
    { icon: 'ri-refresh-line', label: 'Premium Redesign' },
    { icon: 'ri-speed-line', label: 'Performance' },
    { icon: 'ri-robot-2-line', label: 'AI Chatbots' },
    { icon: 'ri-seo-line', label: 'SEO' },
  ];
  return (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <h3 className="text-5xl font-caprasimo mb-6 text-brand-black">Meine Expertise</h3>
          <p className="text-lg text-brand-grey">Alles aus einer Hand für Ihren digitalen Erfolg</p>
        </div>
        <div className="grid md:grid-cols-5 gap-8">
          {skills.map((skill, index) => (
            <motion.div key={index} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: index * 0.1 }} viewport={{ once: true }} className="text-center group">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 bg-brand-beige-light rounded-2xl group-hover:bg-brand-green group-hover:scale-110 transition-all">
                <i className={`${skill.icon} text-3xl text-brand-green group-hover:text-white transition-colors`}></i>
              </div>
              <div className="text-sm font-semibold text-brand-black">{skill.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AboutCTASection() {
  return (
    <section className="py-20 bg-brand-green">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-caprasimo text-white mb-6">
          Bereit für Ihr Projekt?
        </motion.h2>
        <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} viewport={{ once: true }} className="text-lg text-white/90 mb-8">
          Lassen Sie uns gemeinsam Ihre digitale Vision verwirklichen
        </motion.p>
        <motion.a href="/contact" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} viewport={{ once: true }} className="inline-block px-8 py-4 bg-white text-brand-green rounded-full font-semibold hover:bg-brand-beige transition-colors whitespace-nowrap cursor-pointer">
          Jetzt Kontakt aufnehmen
        </motion.a>
      </div>
    </section>
  );
}

const SectionLoader = () => (
  <div className="py-16 flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-brand-green border-t-transparent rounded-full animate-spin" />
  </div>
);

export default function AboutPage() {
  useSEO({
    title: 'Über Benjamin Reick - Webdesigner in Titisee-Neustadt 79822',
    description: 'Benjamin Reick – Webdesigner & AI-Spezialist aus Titisee-Neustadt. Moderne Webseiten, AI-Chatbots & SEO für Unternehmen im Schwarzwald.',
    keywords: 'Benjamin Reick, Webdesigner Titisee-Neustadt, AI-Spezialist 79822, Webentwickler Schwarzwald',
    canonical: '/about',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema('Über Benjamin Reick - Webdesigner & AI-Spezialist', 'Erfahren Sie mehr über Benjamin Reick und seine Expertise in Webdesign und AI-Lösungen', '/about'),
        generatePersonSchema(),
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Startseite', item: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/` },
            { '@type': 'ListItem', position: 2, name: 'Über mich', item: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/about` },
          ],
        },
      ],
    },
  });

  return (
    <div className="min-h-screen bg-white">
      <PageNavbar />
      {/* Hero sofort laden */}
      <section className="relative pt-40 pb-20 overflow-hidden bg-brand-beige-light">
        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <h1 className="text-5xl md:text-7xl font-caprasimo mb-6 text-brand-black">Über mich</h1>
            <p className="text-xl text-brand-grey max-w-3xl mx-auto">
              Ich bin Benjamin Reick – Webdesigner, AI-Spezialist und Ihr Partner für digitale Verkaufsmaschinen.
            </p>
          </motion.div>
        </div>
      </section>
      {/* Restliche Sektionen lazy laden */}
      <Suspense fallback={<SectionLoader />}><AboutMission /></Suspense>
      <Suspense fallback={<SectionLoader />}><AboutValues /></Suspense>
      <Suspense fallback={<SectionLoader />}><AboutExpertise /></Suspense>
      <Suspense fallback={<SectionLoader />}><AboutCTA /></Suspense>
      <Footer />
      <ScrollToTopButton />
    </div>
  );
}
